import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { 
  listPublicDecks, 
  cloneDeck, 
  getGameIcon, 
  getCardgames 
} from '../../services/supabaseService';
import { getGameNameById } from '../../constants/deckRules';
import { exportDeckToText } from '../../utils/deckExporter';
import { useAuth } from '../../components/AuthProvider';
import { useNotification } from '../../components/member/NotificationProvider';
import { CardImage } from '../../components/CardImage';
import { GameSelector } from '../../components/GameSelector';

interface DecksPageProps {
  activeGame: number | 'All';
  setActiveGame: (game: number | 'All') => void;
  dbGames?: { id: any; name: string; slug?: string }[];
}

export const DecksPage: React.FC<DecksPageProps> = ({
  activeGame,
  setActiveGame,
  dbGames = []
}) => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const decksContainerRef = useRef<HTMLDivElement>(null);

  const [decks, setDecks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFormat, setSelectedFormat] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'recent' | 'name' | 'cards' | 'value'>('recent');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [cloningDeckId, setCloningDeckId] = useState<string | null>(null);
  const [openMenuDeckId, setOpenMenuDeckId] = useState<string | null>(null);
  const [gamesList, setGamesList] = useState<{ id: any; name: string; slug?: string }[]>(dbGames);

  // Close open dropdown menu when clicking anywhere outside
  useEffect(() => {
    const handleGlobalClick = () => {
      setOpenMenuDeckId(null);
    };
    window.addEventListener('click', handleGlobalClick);
    return () => window.removeEventListener('click', handleGlobalClick);
  }, []);

  // Pagination for low-end device performance
  const [currentPage, setCurrentPage] = useState(1);
  const [decksPerPage, setDecksPerPage] = useState<number>(18);

  // Reset pagination on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedFormat, sortBy, activeGame]);

  const isGameSelected = activeGame !== 'All' && activeGame !== 0 && Boolean(activeGame);

  // Fetch games list
  useEffect(() => {
    const fetchGames = async () => {
      try {
        const games = await getCardgames();
        if (Array.isArray(games)) setGamesList(games);
      } catch (err) {
        console.warn('Error loading games for decks:', err);
      }
    };
    fetchGames();
  }, []);

  // Load decks for the selected game
  const loadDecks = async () => {
    if (!isGameSelected) {
      setDecks([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await listPublicDecks({
        gameId: activeGame
      });
      setDecks(data || []);
    } catch (err) {
      console.error('Error loading decks:', err);
      showNotification('Erro ao carregar decks.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDecks();
  }, [activeGame, isGameSelected]);

  // Extract formats available from loaded decks
  const availableFormats = useMemo(() => {
    const formats = new Set<string>();
    decks.forEach((d) => {
      const fmt = d.game_formats?.name || d.data?.format_name || d.data?.format;
      if (fmt && typeof fmt === 'string' && fmt.trim()) {
        formats.add(fmt.trim());
      }
    });
    return Array.from(formats);
  }, [decks]);

  // Safe helper to extract string representation from primitives or objects like { name, image }
  const extractStringValue = (val: any): string | null => {
    if (val === null || val === undefined) return null;
    if (typeof val === 'string') return val.trim() || null;
    if (typeof val === 'number') return String(val);
    if (typeof val === 'object') {
      if (typeof val.name === 'string') return val.name.trim() || null;
      if (typeof val.label === 'string') return val.label.trim() || null;
      if (typeof val.value === 'string') return String(val.value).trim() || null;
    }
    return null;
  };

  // Process deck helper statistics
  const processDeckMeta = (deck: any) => {
    const items = deck.data?.items || [];
    const totalCards = deck.data?.total || items.reduce((acc: number, curr: any) => acc + (Number(curr.quantity) || 1), 0);
    const rawFormat = deck.game_formats?.name || deck.data?.format_name || deck.data?.format;
    const format = extractStringValue(rawFormat) || 'Padrão';
    
    // Find leader / first card
    const leaderItem = items.find((i: any) => 
      (typeof i.section === 'string' && i.section.toLowerCase() === 'leader') || 
      (typeof i.card?.type === 'string' && i.card.type.toLowerCase().includes('leader')) ||
      (typeof i.card?.category === 'string' && i.card.category.toLowerCase().includes('leader')) ||
      (typeof i.card?.supertype === 'string' && i.card.supertype.toLowerCase().includes('leader'))
    ) || items[0];

    const leaderCard = leaderItem?.card || null;
    const leaderImage = leaderCard?.imageUrl || leaderCard?.image_url || leaderCard?.image || null;
    const gameName = String(getGameNameById(deck.game_id) || 'Jogo');

    // Calculate estimated deck price
    let estimatedValue = 0;
    items.forEach((i: any) => {
      const c = i.card;
      const qty = Number(i.quantity) || 1;
      let cardPriceVal = Number(c?.price ?? c?.marketPrice ?? 0);
      if ((!cardPriceVal || isNaN(cardPriceVal)) && Array.isArray(c?.variants) && c.variants.length > 0) {
        const vPrices = c.variants
          .map((v: any) => Number(v?.price ?? v?.marketPrice ?? 0))
          .filter((p: number) => !isNaN(p) && p > 0);
        if (vPrices.length > 0) {
          cardPriceVal = Math.min(...vPrices);
        }
      }
      const price = cardPriceVal > 0 ? cardPriceVal : 0;
      if (price > 0 && !isNaN(price)) {
        estimatedValue += price * qty;
      }
    });

    // Detect distinct types/attributes for badges
    const attributes: string[] = [];
    items.forEach((i: any) => {
      const c = i.card;
      if (!c) return;
      const attr = extractStringValue(c.attribute);
      const col = extractStringValue(c.color);
      const elem = extractStringValue(c.element);
      
      if (attr && !attributes.includes(attr)) attributes.push(attr);
      if (col && !attributes.includes(col)) attributes.push(col);
      if (elem && !attributes.includes(elem)) attributes.push(elem);
    });

    return {
      totalCards,
      format,
      leaderCard,
      leaderImage,
      gameName,
      estimatedValue,
      attributes: attributes.slice(0, 3),
      itemsCount: items.length
    };
  };

  // Filtered and sorted decks
  const filteredDecks = useMemo(() => {
    return decks.filter((deck) => {
      // Search query filter (matches deck name or author username/fullname)
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const nameMatch = deck.name?.toLowerCase().includes(q);
        const authorMatch = deck.user?.username?.toLowerCase().includes(q) || deck.user?.fullname?.toLowerCase().includes(q);
        const formatMatch = deck.data?.format?.toLowerCase().includes(q);
        
        // Match key card in deck
        const cardMatch = (deck.data?.items || []).some((item: any) => 
          item.card?.name?.toLowerCase().includes(q)
        );

        if (!nameMatch && !authorMatch && !formatMatch && !cardMatch) {
          return false;
        }
      }

      // Format filter
      if (selectedFormat !== 'all') {
        const deckFormat = deck.data?.format || 'Padrão';
        if (deckFormat.toLowerCase() !== selectedFormat.toLowerCase()) {
          return false;
        }
      }

      return true;
    }).sort((a, b) => {
      if (sortBy === 'name') {
        return (a.name || '').localeCompare(b.name || '');
      }
      if (sortBy === 'cards') {
        const totalA = a.data?.total || (a.data?.items || []).reduce((acc: number, curr: any) => acc + (curr.quantity || 1), 0);
        const totalB = b.data?.total || (b.data?.items || []).reduce((acc: number, curr: any) => acc + (curr.quantity || 1), 0);
        return totalB - totalA;
      }
      if (sortBy === 'value') {
        const metaA = processDeckMeta(a);
        const metaB = processDeckMeta(b);
        return metaB.estimatedValue - metaA.estimatedValue;
      }
      // default: recent
      const dateA = new Date(a.updated_at || a.created_at || 0).getTime();
      const dateB = new Date(b.updated_at || b.created_at || 0).getTime();
      return dateB - dateA;
    });
  }, [decks, searchQuery, selectedFormat, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filteredDecks.length / decksPerPage));
  const paginatedDecks = useMemo(() => {
    if (decksPerPage >= filteredDecks.length) return filteredDecks;
    const start = (currentPage - 1) * decksPerPage;
    return filteredDecks.slice(start, start + decksPerPage);
  }, [filteredDecks, currentPage, decksPerPage]);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    if (decksContainerRef.current) {
      decksContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Clone deck action
  const handleCloneDeck = async (deck: any, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) {
      showNotification('Faça login para clonar este deck para sua coleção.', 'info');
      return;
    }

    setCloningDeckId(deck.id);
    try {
      const newDeckId = await cloneDeck(deck.id);
      showNotification(`Deck "${deck.name}" clonado com sucesso!`, 'success');
      navigate(`/deck/${newDeckId}`);
    } catch (err: any) {
      console.error('Error cloning deck:', err);
      showNotification('Não foi possível clonar o deck.', 'error');
    } finally {
      setCloningDeckId(null);
    }
  };

  // Share link
  const handleShareLink = (deck: any, e: React.MouseEvent) => {
    e.stopPropagation();
    const link = `${window.location.origin}/deck/${deck.id}`;
    navigator.clipboard.writeText(link);
    showNotification('Link do deck copiado para a área de transferência!', 'success');
  };

  // Export deck to text
  const handleExportDeck = (deck: any, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const gameName = getGameNameById(deck.game_id);
      const text = exportDeckToText(gameName, deck.data?.items || []);
      navigator.clipboard.writeText(text);
      showNotification('Lista do deck copiada em texto!', 'success');
    } catch (err) {
      console.error('Error exporting deck:', err);
      showNotification('Erro ao exportar lista do deck.', 'error');
    }
  };

  const currentGameName = useMemo(() => {
    if (!isGameSelected) return '';
    const found = gamesList.find(g => Number(g.id) === Number(activeGame));
    return found?.name || getGameNameById(activeGame);
  }, [activeGame, gamesList, isGameSelected]);

  const currentGameIcon = getGameIcon(activeGame);

  const decksStats = useMemo(() => {
    const totalDecks = decks.length;
    const formatCount = availableFormats.length;
    const authorSet = new Set(decks.map(d => d.user_id || d.author_id || d.author_name).filter(Boolean));
    const totalAuthors = authorSet.size;
    const totalCards = decks.reduce((sum, d) => sum + (Number(d.card_count) || Number(d.data?.total_cards) || 0), 0);
    const avgCards = totalDecks > 0 ? Math.round(totalCards / totalDecks) : 0;

    return {
      totalDecks,
      formatCount,
      totalAuthors,
      avgCards
    };
  }, [decks, availableFormats]);

  // If no game is selected, show standardized game selector (displaying only the name)
  if (!isGameSelected) {
    return (
      <div className="min-h-[75vh] flex items-center justify-center">
        <GameSelector
          games={gamesList}
          onSelectGame={(id) => setActiveGame(id as number)}
          title="Escolha um Jogo"
          subtitle="Para explorar as estratégias e gerenciar decks, selecione uma franquia de TCG:"
        />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6 animate-in fade-in duration-500 px-2 sm:px-4 pb-16">
      {/* Header Principal Padronizado */}
      <div className="flex items-start sm:items-center justify-between gap-3 sm:gap-4 border-b border-slate-800/80 pb-4 sm:pb-6">
        <div className="flex items-start sm:items-center space-x-2.5 sm:space-x-3 min-w-0 flex-1">
          <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-white shadow-lg shadow-purple-600/20 shrink-0">
            <i className="fas fa-layer-group text-base sm:text-lg"></i>
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-black text-white tracking-tight">
              Decks da Comunidade
            </h1>
            <p className="text-xs sm:text-sm text-slate-400 mt-0.5 line-clamp-2">
              Explore estratégias e composições de decks em {currentGameName}
            </p>
          </div>
        </div>

        {/* Game Selector Badges & Quick Action - exatamente igual à pagina meta */}
        <div className="flex items-center gap-2 sm:gap-2.5 shrink-0 pt-0.5 sm:pt-0">
          <button
            onClick={() => setActiveGame('All')}
            className="flex items-center space-x-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-300 hover:text-white text-xs font-bold transition-all cursor-pointer shadow-sm active:scale-95 whitespace-nowrap"
            title="Voltar para a seleção de todos os jogos"
          >
            <i className="fa-solid fa-shapes text-[10px] text-purple-400"></i>
            <span>Trocar Jogo</span>
          </button>
        </div>
      </div>

      {/* KPI Cards de Resumo */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4">
        <div className="bg-slate-900/60 border border-slate-800/80 rounded-xl sm:rounded-2xl p-3 sm:p-4 flex items-center space-x-2.5 sm:space-x-4 min-w-0">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-purple-600/10 border border-purple-500/20 flex items-center justify-center text-purple-400 text-base sm:text-xl shrink-0">
            <i className="fas fa-layer-group"></i>
          </div>
          <div className="min-w-0">
            <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-slate-500 truncate">Total Decks</p>
            <p className="text-lg sm:text-2xl font-black text-white">{decksStats.totalDecks}</p>
          </div>
        </div>

        <div className="bg-slate-900/60 border border-slate-800/80 rounded-xl sm:rounded-2xl p-3 sm:p-4 flex items-center space-x-2.5 sm:space-x-4 min-w-0">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center text-blue-400 text-base sm:text-xl shrink-0">
            <i className="fas fa-award"></i>
          </div>
          <div className="min-w-0">
            <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-slate-500 truncate">Formatos</p>
            <p className="text-lg sm:text-2xl font-black text-blue-400">{decksStats.formatCount}</p>
          </div>
        </div>

        <div className="bg-slate-900/60 border border-slate-800/80 rounded-xl sm:rounded-2xl p-3 sm:p-4 flex items-center space-x-2.5 sm:space-x-4 min-w-0">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-emerald-600/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 text-base sm:text-xl shrink-0">
            <i className="fas fa-users"></i>
          </div>
          <div className="min-w-0">
            <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-slate-500 truncate">Criadores</p>
            <p className="text-lg sm:text-2xl font-black text-emerald-400">{decksStats.totalAuthors}</p>
          </div>
        </div>

        <div className="bg-slate-900/60 border border-slate-800/80 rounded-xl sm:rounded-2xl p-3 sm:p-4 flex items-center space-x-2.5 sm:space-x-4 min-w-0">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-amber-600/10 border border-amber-500/20 flex items-center justify-center text-amber-400 text-base sm:text-xl shrink-0">
            <i className="fas fa-clone"></i>
          </div>
          <div className="min-w-0">
            <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-slate-500 truncate">Média de Cartas</p>
            <p className="text-lg sm:text-2xl font-black text-amber-400">{decksStats.avgCards}</p>
          </div>
        </div>
      </div>

      {/* Search and Filters Toolbar */}
      <div className="bg-slate-900/90 border border-slate-800 p-3.5 sm:p-4 rounded-2xl flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 sm:gap-4 shadow-sm">
        {/* Search Bar */}
        <div className="relative w-full md:w-80 lg:w-96">
          <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs pointer-events-none"></i>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar por nome do deck, carta ou criador..."
            className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-9 pr-9 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 transition-colors"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white p-1 cursor-pointer"
            >
              <i className="fas fa-times text-xs"></i>
            </button>
          )}
        </div>

        {/* Filter Selects & View Mode - Responsive Grid on Mobile, Flex on Desktop (Zero Horizontal Scroll) */}
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2 w-full md:w-auto">
          {/* Format Filter */}
          {availableFormats.length > 0 && (
            <div className="relative w-full sm:w-auto">
              <select
                value={selectedFormat}
                onChange={(e) => setSelectedFormat(e.target.value)}
                aria-label="Filtrar por Formato"
                className="w-full sm:w-auto bg-slate-950 border border-slate-800 text-slate-300 text-xs rounded-xl px-3 py-2 focus:outline-none focus:border-purple-500 appearance-none pr-8 cursor-pointer font-bold truncate"
              >
                <option value="all">Todos os Formatos</option>
                {availableFormats.map((fmt) => (
                  <option key={fmt} value={fmt}>
                    {fmt}
                  </option>
                ))}
              </select>
              <i className="fas fa-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-500 pointer-events-none"></i>
            </div>
          )}

          {/* Sort By Dropdown */}
          <div className="relative w-full sm:w-auto">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              aria-label="Ordenar por"
              className="w-full sm:w-auto bg-slate-950 border border-slate-800 text-slate-300 text-xs rounded-xl px-3 py-2 focus:outline-none focus:border-purple-500 appearance-none pr-8 cursor-pointer font-bold truncate"
            >
              <option value="recent">Mais Recentes</option>
              <option value="name">Nome (A-Z)</option>
              <option value="cards">Mais Cartas</option>
              <option value="value">Maior Valor</option>
            </select>
            <i className="fas fa-sort absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-500 pointer-events-none"></i>
          </div>

          {/* View Mode Toggle */}
          <div className="col-span-2 sm:col-span-1 flex items-center justify-end sm:justify-start bg-slate-950 border border-slate-800 rounded-xl p-0.5 shrink-0 ml-auto sm:ml-0">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-2.5 py-1.5 rounded-lg text-xs transition-colors flex items-center gap-1.5 cursor-pointer ${
                viewMode === 'grid' ? 'bg-purple-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'
              }`}
              title="Visualização em Grade"
            >
              <i className="fas fa-th-large text-[11px]"></i>
              <span className="sm:hidden text-[10px] font-bold">Grade</span>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-2.5 py-1.5 rounded-lg text-xs transition-colors flex items-center gap-1.5 cursor-pointer ${
                viewMode === 'list' ? 'bg-purple-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'
              }`}
              title="Visualização em Lista"
            >
              <i className="fas fa-list text-[11px]"></i>
              <span className="sm:hidden text-[10px] font-bold">Lista</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content: Decks List or Grid */}
      {loading ? (
        <div className="py-20 flex flex-col items-center justify-center space-y-4 text-center">
          <div className="w-12 h-12 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin"></div>
          <p className="text-sm font-bold text-slate-400">Carregando decks...</p>
        </div>
      ) : filteredDecks.length === 0 ? (
        <div className="py-20 bg-slate-900/40 border border-white/5 rounded-3xl flex flex-col items-center justify-center space-y-4 text-center p-6">
          <div className="w-16 h-16 rounded-2xl bg-purple-950/40 border border-purple-500/20 flex items-center justify-center text-purple-400 text-2xl">
            <i className="fas fa-layer-group"></i>
          </div>
          <div className="space-y-1">
            <h3 className="text-lg font-black text-white uppercase italic">
              Nenhum deck encontrado
            </h3>
            <p className="text-xs text-slate-400 max-w-md">
              {searchQuery || selectedFormat !== 'all'
                ? 'Tente alterar os filtros ou o termo de busca para encontrar outros decks.'
                : 'Ainda não há decks registrados para este jogo. Seja o primeiro a compartilhar sua estratégia!'}
            </p>
          </div>
          <div className="flex items-center space-x-3 pt-2">
            {(searchQuery || selectedFormat !== 'all') && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedFormat('all');
                }}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-black uppercase tracking-wider rounded-xl transition-all"
              >
                Limpar Filtros
              </button>
            )}
            <Link
              to="/meus-decks?novo=1"
              className="px-5 py-2.5 bg-purple-600 hover:bg-purple-500 text-white text-xs font-black uppercase tracking-wider rounded-xl shadow-lg shadow-purple-600/30 transition-all"
            >
              Criar Novo Baralho
            </Link>
          </div>
        </div>
      ) : (
        <div ref={decksContainerRef} className="scroll-mt-6">
          {viewMode === 'grid' ? (
            /* Grid Layout */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {paginatedDecks.map((deck) => {
                const meta = processDeckMeta(deck);
                const isCloning = cloningDeckId === deck.id;
                const isOwner = Boolean(user && (deck.user_id === user.id || deck.user?.id === user.id));

                return (
                  <motion.div
                    key={deck.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.15 }}
                    className={`bg-slate-900 border border-slate-800 hover:border-purple-500/40 rounded-2xl flex flex-col justify-between transition-all duration-200 shadow-md hover:shadow-purple-600/10 group relative ${
                      openMenuDeckId === deck.id ? 'z-30 border-purple-500/50' : ''
                    }`}
                  >
                    {/* Top Section: Card Artwork Hero Header */}
                    <div className="relative h-36 sm:h-40 w-full bg-slate-950 overflow-hidden rounded-t-2xl">
                      {meta.leaderImage ? (
                        <>
                          {/* Lightweight background layer without expensive Gaussian blur filter */}
                          <div
                            className="absolute inset-0 bg-cover bg-center opacity-15 pointer-events-none"
                            style={{ backgroundImage: `url(${meta.leaderImage})` }}
                          ></div>
                          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/70 to-slate-950/80 pointer-events-none"></div>

                          {/* Floating Leader Card Art */}
                          <div className="absolute right-3 sm:right-4 top-2.5 w-18 sm:w-20 h-28 sm:h-32 rounded-lg overflow-hidden border border-white/20 shadow-md group-hover:scale-105 transition-transform duration-300">
                            <CardImage
                              src={meta.leaderImage}
                              alt={meta.leaderCard?.name || deck.name}
                              objectFit="cover"
                              className="w-full h-full"
                            />
                          </div>
                        </>
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-purple-950/40 to-slate-950">
                          <i className={`${currentGameIcon} text-4xl sm:text-5xl text-white/10`}></i>
                        </div>
                      )}

                      {/* Badges and Name on artwork */}
                      <div className="relative z-10 p-3.5 sm:p-4 flex flex-col justify-between h-full max-w-[calc(100%-5.5rem)] sm:max-w-[calc(100%-6rem)]">
                        <div className="flex items-center space-x-1.5 flex-wrap gap-y-1">
                          {meta.format && (
                            <span className="px-2 py-0.5 bg-slate-900/80 text-slate-300 text-[9px] font-bold uppercase rounded border border-slate-700">
                              {meta.format}
                            </span>
                          )}
                        </div>

                        <div className="space-y-1">
                          <Link
                            to={`/deck/${deck.id}`}
                            className="text-sm sm:text-base font-black text-white hover:text-purple-300 transition-colors uppercase tracking-tight line-clamp-2 italic"
                          >
                            {deck.name}
                          </Link>
                        </div>
                      </div>
                    </div>

                    {/* Middle Info & Stats Body */}
                    <div className="p-3.5 sm:p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-2 bg-slate-950/60 p-2.5 rounded-xl border border-slate-800 text-center">
                        <div>
                          <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest block">
                            Total de Cartas
                          </span>
                          <span className="text-xs font-black text-slate-200 font-mono tabular-nums">
                            {meta.totalCards} cartas
                          </span>
                        </div>
                        <div>
                          <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest block">
                            Valor Estimado
                          </span>
                          <span className={`text-xs font-black font-mono tabular-nums ${meta.estimatedValue > 0 ? 'text-emerald-400' : 'text-slate-500'}`}>
                            {meta.estimatedValue > 0 ? `R$ ${meta.estimatedValue.toFixed(2)}` : 'S/ Preço'}
                          </span>
                        </div>
                      </div>

                      {/* Creator Info & Date */}
                      <div className="flex items-center justify-between pt-1 text-[11px] text-slate-400">
                        <Link
                          to={deck.user?.username ? `/perfil/${deck.user.username}` : (deck.user?.id ? `/perfil/${deck.user.id}` : '#')}
                          onClick={(e) => {
                            if (!deck.user?.username && !deck.user?.id) e.preventDefault();
                            e.stopPropagation();
                          }}
                          className="flex items-center space-x-2 min-w-0 hover:text-purple-300 transition-colors group/author"
                          title={deck.user?.username ? `Ver perfil de @${deck.user.username}` : (deck.user?.fullname || 'Ver perfil')}
                        >
                          <div className="w-5 h-5 rounded-full overflow-hidden bg-purple-950 border border-purple-500/30 flex items-center justify-center shrink-0 group-hover/author:border-purple-400 transition-colors">
                            {deck.user?.avatar ? (
                              <img
                                src={deck.user.avatar}
                                alt={deck.user.username || 'Autor'}
                                className="w-full h-full object-cover"
                                referrerPolicy="no-referrer"
                              />
                            ) : (
                              <i className="fas fa-user text-[9px] text-purple-300"></i>
                            )}
                          </div>
                          <span className="truncate font-bold text-slate-300 group-hover/author:text-purple-300">
                            {deck.user?.username ? `@${deck.user.username}` : (deck.user?.fullname || 'Jogador Anônimo')}
                          </span>
                        </Link>

                        <span className="text-[10px] text-slate-500 shrink-0 font-mono tabular-nums">
                          {new Date(deck.updated_at || deck.created_at).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    </div>

                    {/* Card Action Footer */}
                    <div className="p-3 bg-slate-950/70 border-t border-slate-800 flex items-center justify-between gap-1.5">
                      <Link
                        to={`/deck/${deck.id}`}
                        className="flex-1 py-2 px-3 bg-purple-600 hover:bg-purple-500 text-white font-black text-[10px] uppercase tracking-wider rounded-lg text-center transition-all flex items-center justify-center space-x-1.5 shadow-sm active:scale-95"
                      >
                        <i className="fas fa-eye text-[9px]"></i>
                        <span>Ver Deck</span>
                      </Link>

                      {isOwner && (
                        <button
                          type="button"
                          onClick={() => {
                            if (setActiveGame && deck.game_id) {
                              setActiveGame(Number(deck.game_id));
                            }
                            navigate(`/deckbuilder?deckId=${deck.id}`);
                          }}
                          className="py-2 px-2.5 bg-slate-800 hover:bg-purple-600/30 text-purple-300 hover:text-white font-bold text-[10px] uppercase tracking-wider rounded-lg border border-slate-700 transition-all flex items-center justify-center space-x-1 cursor-pointer active:scale-95"
                          title="Editar cartas no Deckbuilder"
                        >
                          <i className="fas fa-hammer text-[9px]"></i>
                          <span className="hidden xs:inline">Editar</span>
                        </button>
                      )}

                      {/* Botão Opções com Dropdown */}
                      <div className="relative">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuDeckId(openMenuDeckId === deck.id ? null : deck.id);
                          }}
                          className={`p-2 px-2.5 rounded-lg border text-xs font-bold transition-all flex items-center gap-1 cursor-pointer active:scale-95 ${
                            openMenuDeckId === deck.id
                              ? 'bg-purple-600 text-white border-purple-500 shadow-sm'
                              : 'bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border-slate-700'
                          }`}
                          title="Mais opções do baralho"
                        >
                          <i className="fas fa-ellipsis-v text-[10px]"></i>
                          <span className="text-[10px] font-bold">Opções</span>
                        </button>

                        <AnimatePresence>
                          {openMenuDeckId === deck.id && (
                            <motion.div
                              initial={{ opacity: 0, scale: 0.95, y: 6 }}
                              animate={{ opacity: 1, scale: 1, y: 0 }}
                              exit={{ opacity: 0, scale: 0.95, y: 6 }}
                              transition={{ duration: 0.12 }}
                              onClick={(e) => e.stopPropagation()}
                              className="absolute right-0 bottom-full mb-2 w-56 bg-slate-950 border border-slate-800 rounded-2xl shadow-2xl p-1.5 z-50 space-y-0.5"
                            >
                              <Link
                                to={`/deck/${deck.id}`}
                                onClick={() => setOpenMenuDeckId(null)}
                                className="w-full text-left px-3 py-2 rounded-xl text-xs font-bold text-slate-200 hover:text-white hover:bg-purple-600/15 transition-colors flex items-center gap-2.5 cursor-pointer"
                              >
                                <i className="fas fa-eye text-blue-400 w-4 text-center"></i>
                                <span>Visualizar Deck</span>
                              </Link>

                              {isOwner && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setOpenMenuDeckId(null);
                                    if (setActiveGame && deck.game_id) {
                                      setActiveGame(Number(deck.game_id));
                                    }
                                    navigate(`/deckbuilder?deckId=${deck.id}`);
                                  }}
                                  className="w-full text-left px-3 py-2 rounded-xl text-xs font-bold text-slate-200 hover:text-white hover:bg-purple-600/15 transition-colors flex items-center gap-2.5 cursor-pointer"
                                >
                                  <i className="fas fa-hammer text-purple-400 w-4 text-center"></i>
                                  <span>Editar no Deckbuilder</span>
                                </button>
                              )}

                              <button
                                type="button"
                                onClick={(e) => {
                                  setOpenMenuDeckId(null);
                                  handleCloneDeck(deck, e);
                                }}
                                disabled={isCloning}
                                className="w-full text-left px-3 py-2 rounded-xl text-xs font-bold text-slate-200 hover:text-white hover:bg-purple-600/15 transition-colors flex items-center gap-2.5 cursor-pointer disabled:opacity-50"
                              >
                                <i className={`fas ${isCloning ? 'fa-spinner fa-spin' : 'fa-copy'} text-indigo-400 w-4 text-center`}></i>
                                <span>Clonar para Meus Decks</span>
                              </button>

                              <button
                                type="button"
                                onClick={(e) => {
                                  setOpenMenuDeckId(null);
                                  handleExportDeck(deck, e);
                                }}
                                className="w-full text-left px-3 py-2 rounded-xl text-xs font-bold text-slate-200 hover:text-white hover:bg-purple-600/15 transition-colors flex items-center gap-2.5 cursor-pointer"
                              >
                                <i className="fas fa-file-export text-emerald-400 w-4 text-center"></i>
                                <span>Copiar Lista (Texto)</span>
                              </button>

                              <button
                                type="button"
                                onClick={(e) => {
                                  setOpenMenuDeckId(null);
                                  handleShareLink(deck, e);
                                }}
                                className="w-full text-left px-3 py-2 rounded-xl text-xs font-bold text-slate-200 hover:text-white hover:bg-purple-600/15 transition-colors flex items-center gap-2.5 cursor-pointer"
                              >
                                <i className="fas fa-share-alt text-amber-400 w-4 text-center"></i>
                                <span>Compartilhar Link</span>
                              </button>

                              {isOwner && (
                                <>
                                  <div className="my-1 border-t border-slate-800/80"></div>
                                  <Link
                                    to="/meus-decks"
                                    onClick={() => setOpenMenuDeckId(null)}
                                    className="w-full text-left px-3 py-2 rounded-xl text-xs font-bold text-purple-300 hover:text-purple-200 hover:bg-purple-600/15 transition-colors flex items-center gap-2.5 cursor-pointer"
                                  >
                                    <i className="fas fa-layer-group text-purple-400 w-4 text-center"></i>
                                    <span>Gerenciar em Meus Decks</span>
                                  </Link>
                                </>
                              )}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            /* List Layout */
            <div className="space-y-3">
              {paginatedDecks.map((deck) => {
                const meta = processDeckMeta(deck);
                const isCloning = cloningDeckId === deck.id;
                const isOwner = Boolean(user && (deck.user_id === user.id || deck.user?.id === user.id));

                return (
                  <motion.div
                    key={deck.id}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`bg-slate-900 border border-slate-800 hover:border-purple-500/30 p-3 sm:p-4 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 transition-all relative ${
                      openMenuDeckId === deck.id ? 'z-30 border-purple-500/40' : ''
                    }`}
                  >
                    <div className="flex items-center space-x-3 min-w-0">
                      {/* Thumbnail */}
                      <div className="w-12 h-16 rounded-lg overflow-hidden bg-slate-950 border border-slate-800 shrink-0 flex items-center justify-center shadow">
                        {meta.leaderImage ? (
                          <CardImage
                            src={meta.leaderImage}
                            alt={deck.name}
                            objectFit="cover"
                            className="w-full h-full"
                          />
                        ) : (
                          <i className={`${currentGameIcon} text-purple-400 text-xl`}></i>
                        )}
                      </div>

                      <div className="min-w-0 space-y-1">
                        <div className="flex items-center space-x-2 flex-wrap gap-y-0.5">
                          {meta.format && (
                            <span className="px-1.5 py-0.5 bg-slate-800 text-slate-300 text-[8.5px] font-bold uppercase rounded border border-slate-700">
                              {meta.format}
                            </span>
                          )}

                          <span className="text-[10px] text-slate-500 font-mono tabular-nums font-bold">
                            {meta.totalCards} cartas
                          </span>
                        </div>

                        <Link
                          to={`/deck/${deck.id}`}
                          className="text-sm font-black text-white hover:text-purple-300 transition-colors uppercase tracking-tight block truncate italic"
                        >
                          {deck.name}
                        </Link>

                        <div className="flex items-center space-x-2 text-[10px] text-slate-500 font-mono tabular-nums">
                          <Link
                            to={deck.user?.username ? `/perfil/${deck.user.username}` : (deck.user?.id ? `/perfil/${deck.user.id}` : '#')}
                            onClick={(e) => {
                              if (!deck.user?.username && !deck.user?.id) e.preventDefault();
                              e.stopPropagation();
                            }}
                            className="flex items-center space-x-1.5 hover:text-purple-300 transition-colors group/author"
                            title={deck.user?.username ? `Ver perfil de @${deck.user.username}` : (deck.user?.fullname || 'Ver perfil')}
                          >
                            <div className="w-4 h-4 rounded-full overflow-hidden bg-purple-950 border border-purple-500/30 flex items-center justify-center shrink-0 group-hover/author:border-purple-400 transition-colors">
                              {deck.user?.avatar ? (
                                <img
                                  src={deck.user.avatar}
                                  alt={deck.user.username || 'Autor'}
                                  className="w-full h-full object-cover"
                                  referrerPolicy="no-referrer"
                                />
                              ) : (
                                <i className="fas fa-user text-[8px] text-purple-300"></i>
                              )}
                            </div>
                            <span className="font-bold text-slate-300 group-hover/author:text-purple-300">
                              {deck.user?.username ? `@${deck.user.username}` : (deck.user?.fullname || 'Jogador')}
                            </span>
                          </Link>
                          <span>·</span>
                          <span>{new Date(deck.updated_at || deck.created_at).toLocaleDateString('pt-BR')}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between sm:justify-end gap-2 w-full sm:w-auto shrink-0 pt-2.5 sm:pt-0 border-t sm:border-t-0 border-slate-800">
                      {meta.estimatedValue > 0 && (
                        <span className="text-xs font-black text-emerald-400 font-mono tabular-nums">
                          R$ {meta.estimatedValue.toFixed(2)}
                        </span>
                      )}

                      <div className="flex items-center gap-2 ml-auto sm:ml-0">
                        <Link
                          to={`/deck/${deck.id}`}
                          className="px-3 py-1.5 bg-purple-600 hover:bg-purple-500 text-white font-black text-[10px] uppercase tracking-wider rounded-lg transition-all flex items-center space-x-1.5 shadow-sm active:scale-95"
                        >
                          <span>Ver Deck</span>
                        </Link>

                        {/* Botão Opções com Dropdown */}
                        <div className="relative">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMenuDeckId(openMenuDeckId === deck.id ? null : deck.id);
                            }}
                            className={`p-1.5 px-2.5 rounded-lg border text-xs font-bold transition-all flex items-center gap-1 cursor-pointer active:scale-95 ${
                              openMenuDeckId === deck.id
                                ? 'bg-purple-600 text-white border-purple-500 shadow-sm'
                                : 'bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border-slate-700'
                            }`}
                            title="Mais opções do baralho"
                          >
                            <i className="fas fa-ellipsis-v text-[10px]"></i>
                            <span className="text-[10px] font-bold">Opções</span>
                          </button>

                          <AnimatePresence>
                            {openMenuDeckId === deck.id && (
                              <motion.div
                                initial={{ opacity: 0, scale: 0.95, y: 6 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95, y: 6 }}
                                transition={{ duration: 0.12 }}
                                onClick={(e) => e.stopPropagation()}
                                className="absolute right-0 bottom-full mb-2 w-56 bg-slate-950 border border-slate-800 rounded-2xl shadow-2xl p-1.5 z-50 space-y-0.5"
                              >
                                <Link
                                  to={`/deck/${deck.id}`}
                                  onClick={() => setOpenMenuDeckId(null)}
                                  className="w-full text-left px-3 py-2 rounded-xl text-xs font-bold text-slate-200 hover:text-white hover:bg-purple-600/15 transition-colors flex items-center gap-2.5 cursor-pointer"
                                >
                                  <i className="fas fa-eye text-blue-400 w-4 text-center"></i>
                                  <span>Visualizar Deck</span>
                                </Link>

                                {isOwner && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setOpenMenuDeckId(null);
                                      if (setActiveGame && deck.game_id) {
                                        setActiveGame(Number(deck.game_id));
                                      }
                                      navigate(`/deckbuilder?deckId=${deck.id}`);
                                    }}
                                    className="w-full text-left px-3 py-2 rounded-xl text-xs font-bold text-slate-200 hover:text-white hover:bg-purple-600/15 transition-colors flex items-center gap-2.5 cursor-pointer"
                                  >
                                    <i className="fas fa-hammer text-purple-400 w-4 text-center"></i>
                                    <span>Editar no Deckbuilder</span>
                                  </button>
                                )}

                                <button
                                  type="button"
                                  onClick={(e) => {
                                    setOpenMenuDeckId(null);
                                    handleCloneDeck(deck, e);
                                  }}
                                  disabled={isCloning}
                                  className="w-full text-left px-3 py-2 rounded-xl text-xs font-bold text-slate-200 hover:text-white hover:bg-purple-600/15 transition-colors flex items-center gap-2.5 cursor-pointer disabled:opacity-50"
                                >
                                  <i className={`fas ${isCloning ? 'fa-spinner fa-spin' : 'fa-copy'} text-indigo-400 w-4 text-center`}></i>
                                  <span>Clonar para Meus Decks</span>
                                </button>

                                <button
                                  type="button"
                                  onClick={(e) => {
                                    setOpenMenuDeckId(null);
                                    handleExportDeck(deck, e);
                                  }}
                                  className="w-full text-left px-3 py-2 rounded-xl text-xs font-bold text-slate-200 hover:text-white hover:bg-purple-600/15 transition-colors flex items-center gap-2.5 cursor-pointer"
                                >
                                  <i className="fas fa-file-export text-emerald-400 w-4 text-center"></i>
                                  <span>Copiar Lista (Texto)</span>
                                </button>

                                <button
                                  type="button"
                                  onClick={(e) => {
                                    setOpenMenuDeckId(null);
                                    handleShareLink(deck, e);
                                  }}
                                  className="w-full text-left px-3 py-2 rounded-xl text-xs font-bold text-slate-200 hover:text-white hover:bg-purple-600/15 transition-colors flex items-center gap-2.5 cursor-pointer"
                                >
                                  <i className="fas fa-share-alt text-amber-400 w-4 text-center"></i>
                                  <span>Compartilhar Link</span>
                                </button>

                                {isOwner && (
                                  <>
                                    <div className="my-1 border-t border-slate-800/80"></div>
                                    <Link
                                      to="/meus-decks"
                                      onClick={() => setOpenMenuDeckId(null)}
                                      className="w-full text-left px-3 py-2 rounded-xl text-xs font-bold text-purple-300 hover:text-purple-200 hover:bg-purple-600/15 transition-colors flex items-center gap-2.5 cursor-pointer"
                                    >
                                      <i className="fas fa-layer-group text-purple-400 w-4 text-center"></i>
                                      <span>Gerenciar em Meus Decks</span>
                                    </Link>
                                  </>
                                )}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Pagination Controls */}
          {filteredDecks.length > 0 && (
            <div className="mt-6 pt-5 border-t border-slate-800/80 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-xs text-slate-400 font-medium">
                Mostrando <span className="font-bold text-white">{(currentPage - 1) * decksPerPage + 1}</span> a <span className="font-bold text-white">{Math.min(filteredDecks.length, currentPage * decksPerPage)}</span> de <span className="font-bold text-white">{filteredDecks.length}</span> decks
              </div>

              {totalPages > 1 && (
                <div className="flex flex-wrap items-center justify-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-800 text-slate-300 hover:text-white hover:border-purple-500/50 disabled:opacity-30 disabled:cursor-not-allowed transition-all text-xs font-bold flex items-center gap-1.5 cursor-pointer"
                    aria-label="Página anterior"
                  >
                    <i className="fas fa-chevron-left text-[10px]"></i>
                    <span className="hidden sm:inline">Anterior</span>
                  </button>

                  <div className="flex flex-wrap items-center justify-center gap-1">
                    {Array.from({ length: totalPages }).map((_, idx) => {
                      const pageNum = idx + 1;
                      if (
                        pageNum === 1 ||
                        pageNum === totalPages ||
                        (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={pageNum}
                            type="button"
                            onClick={() => handlePageChange(pageNum)}
                            className={`min-w-8 h-8 px-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                              currentPage === pageNum
                                ? 'bg-purple-600 text-white shadow-md shadow-purple-600/30'
                                : 'bg-slate-900 border border-slate-800 text-slate-400 hover:text-white hover:border-slate-700'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      } else if (
                        pageNum === currentPage - 2 ||
                        pageNum === currentPage + 2
                      ) {
                        return (
                          <span key={pageNum} className="text-slate-600 text-xs px-1">
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                  </div>

                  <button
                    type="button"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-800 text-slate-300 hover:text-white hover:border-purple-500/50 disabled:opacity-30 disabled:cursor-not-allowed transition-all text-xs font-bold flex items-center gap-1.5 cursor-pointer"
                    aria-label="Próxima página"
                  >
                    <span className="hidden sm:inline">Próxima</span>
                    <i className="fas fa-chevron-right text-[10px]"></i>
                  </button>
                </div>
              )}

              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span>Por página:</span>
                <select
                  value={decksPerPage}
                  onChange={e => {
                    setDecksPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="bg-slate-900 border border-slate-800 rounded-lg px-2.5 py-1 text-slate-200 text-xs font-semibold focus:outline-none focus:border-purple-500 cursor-pointer"
                >
                  <option value={9}>9</option>
                  <option value={18}>18</option>
                  <option value={36}>36</option>
                  <option value={9999}>Todos</option>
                </select>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
